// Phase APP-4 · Resubmit modal — works for BOTH micro_hub_suggestion and
// packer_request. Pre-fills entity_payload, shows the rejection banner so
// the submitter knows what to fix, then POSTs /api/approvals/{id}/resubmit.
import { useEffect, useMemo, useState } from "react";
import { X, Loader2, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../../services/api";

const ORANGE = "#FF6B00";
const SHIFTS = ["morning", "evening", "night", "flexible"];

export default function ResubmitRequestModal({ open, onClose, request, onDone }) {
  const isPacker = request?.approval_type === "packer_request";
  const initial = useMemo(() => request?.entity_payload || {}, [request]);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open && request) {
      setForm({ ...initial });
      setBusy(false); setErr("");
    }
  }, [open, initial, request]);

  if (!open || !request) return null;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onBackdrop = (e) => { if (e.target === e.currentTarget && !busy) onClose?.(); };

  const valid = isPacker
    ? ((form.full_name || "").trim().length >= 3
        && (form.full_name || "").trim().length <= 80
        && /^\d{10}$/.test((form.phone || "").trim())
        && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((form.email || "").trim())
        && SHIFTS.includes(form.shift_timing))
    : ((form.name || "").trim().length >= 3
        && (form.name || "").trim().length <= 120
        && (form.city || "").trim().length > 0
        && /^\d{6}$/.test((form.pincode || "").trim())
        && (form.address || "").trim().length > 0
        && Number(form.expected_capacity) >= 1
        && Number(form.expected_capacity) <= 10000);

  const lastReject = (request.approval_history || [])
                        .slice().reverse()
                        .find((h) => h.action === "rejected");

  const submit = async () => {
    if (!valid) return;
    setBusy(true); setErr("");
    try {
      let body;
      if (isPacker) {
        body = {
          full_name: form.full_name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim().toLowerCase(),
          micro_hub_id: initial.micro_hub_id || form.micro_hub_id,
          shift_timing: form.shift_timing,
          capability: initial.capability || form.capability || ["packing"],
          remarks: (form.remarks || "").trim(),
        };
      } else {
        body = {
          name: form.name.trim(),
          city: form.city.trim(),
          pincode: form.pincode.trim(),
          address: form.address.trim(),
          expected_capacity: Number(form.expected_capacity),
          remarks: (form.remarks || "").trim(),
        };
        if ((form.assigned_lead_user_id || "").trim()) {
          body.assigned_lead_user_id = form.assigned_lead_user_id.trim();
        }
      }
      const r = await api.post(`/approvals/${request.request_id}/resubmit`, body);
      toast.success("Resubmitted · awaiting review");
      onDone?.(r.data?.status);
      onClose?.();
    } catch (e2) {
      const detail = e2?.response?.data?.detail || e2?.message || "Failed";
      setErr(typeof detail === "string" ? detail : JSON.stringify(detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 p-2"
         onClick={onBackdrop}
         data-testid="resubmit-request-modal"
         data-approval-type={request.approval_type}
         role="dialog" aria-modal="true">
      <div className="bg-white w-full sm:max-w-lg rounded-2xl shadow-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
          <RefreshCw className="h-4 w-4" style={{ color: ORANGE }}/>
          <h3 className="text-base font-semibold flex-1">
            Resubmit · {isPacker ? "Packer Request" : "Micro Hub Suggestion"}
          </h3>
          <button onClick={onClose} aria-label="Close"
                  data-testid="resubmit-close"
                  className="p-1.5 rounded-full hover:bg-slate-100" disabled={busy}>
            <X className="h-4 w-4"/>
          </button>
        </div>
        <div className="px-4 py-3 space-y-3 max-h-[70vh] overflow-y-auto">
          {lastReject && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs"
                 data-testid="resubmit-rejection-banner">
              <div className="font-semibold text-rose-700 mb-0.5">
                Rejection reason — {lastReject.actor_name}
              </div>
              {lastReject.rejection_reason?.category && (
                <div className="text-rose-800">
                  Category: <span className="font-mono">{lastReject.rejection_reason.category}</span>
                </div>
              )}
              {lastReject.rejection_reason?.remarks && (
                <div className="text-rose-800 whitespace-pre-wrap break-words mt-0.5">
                  {lastReject.rejection_reason.remarks}
                </div>
              )}
              <div className="text-rose-400 text-[10px] mt-0.5">{lastReject.ts}</div>
            </div>
          )}
          {err && (
            <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-xs text-rose-700 inline-flex items-start gap-1.5"
                 data-testid="resubmit-error">
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0"/> {err}
            </div>
          )}
          {isPacker ? (
            <>
              <Field label="Full name *">
                <input value={form.full_name || ""} onChange={(e) => set("full_name", e.target.value)}
                       data-testid="resubmit-packer-name"
                       className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Phone *">
                  <input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)}
                         maxLength={10} data-testid="resubmit-packer-phone"
                         className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"/>
                </Field>
                <Field label="Shift *">
                  <select value={form.shift_timing || "morning"}
                          onChange={(e) => set("shift_timing", e.target.value)}
                          data-testid="resubmit-packer-shift"
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm">
                    {SHIFTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="Email *">
                <input value={form.email || ""} onChange={(e) => set("email", e.target.value)}
                       data-testid="resubmit-packer-email"
                       className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
              </Field>
              <Field label="Remarks">
                <textarea value={form.remarks || ""} onChange={(e) => set("remarks", e.target.value)}
                          rows={2} maxLength={500} data-testid="resubmit-packer-remarks"
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm resize-none"/>
              </Field>
            </>
          ) : (
            <>
              <Field label="Name *">
                <input value={form.name || ""} onChange={(e) => set("name", e.target.value)}
                       data-testid="resubmit-hub-name"
                       className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
              </Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="City *">
                  <input value={form.city || ""} onChange={(e) => set("city", e.target.value)}
                         data-testid="resubmit-hub-city"
                         className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
                </Field>
                <Field label="Pincode *">
                  <input value={form.pincode || ""} onChange={(e) => set("pincode", e.target.value)}
                         maxLength={6} data-testid="resubmit-hub-pincode"
                         className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm font-mono"/>
                </Field>
              </div>
              <Field label="Address *">
                <textarea value={form.address || ""} onChange={(e) => set("address", e.target.value)}
                          rows={2} data-testid="resubmit-hub-address"
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm resize-none"/>
              </Field>
              <Field label="Expected capacity *">
                <input type="number" min={1} max={10000}
                       value={form.expected_capacity || ""}
                       onChange={(e) => set("expected_capacity", e.target.value)}
                       data-testid="resubmit-hub-capacity"
                       className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"/>
              </Field>
              <Field label="Remarks">
                <textarea value={form.remarks || ""} onChange={(e) => set("remarks", e.target.value)}
                          rows={2} maxLength={500} data-testid="resubmit-hub-remarks"
                          className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm resize-none"/>
              </Field>
            </>
          )}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  data-testid="resubmit-cancel"
                  className="text-sm px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
            Cancel
          </button>
          <button onClick={submit}
                  disabled={!valid || busy}
                  data-testid="resubmit-submit"
                  data-can-submit={valid && !busy ? "true" : "false"}
                  className="text-sm font-semibold px-4 py-2 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                  style={{ backgroundColor: ORANGE }}>
            {busy && <Loader2 className="h-3.5 w-3.5 animate-spin"/>}
            Resubmit
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500">{label}</label>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
